<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="subTitle">
        <template #afterTitle>
          <div class="d-flex gap-2">
            <McListDownloader
              :pageOptions="downloadPageOptions"
              :fileFormatOptions="['excel']"
              @download="handleDownload"
            />
            <McButton
              variant="primary"
              :text="$t('global.addField')"
              icon="icon-fill_plus"
              @click="handleAdd"
            />
          </div>
        </template>
      </PageHeader>
    </div>

    <McGrapqlTable
      :apiClient="query"
      :fields="fields"
      :hasCheckbox="true"
      :hasDelete="false"
      :searchable="true"
      :withCard="true"
      trackBy="zoneId"
      :isSelectableField="false"
      :stickyHeader="true"
      :stickyCheckbox="true"
      :activeFilters="activeFilters"
      :hasSelectAll="false"
      :customButton="true"
      :hasFilter="true"
      displayDeleteBy="name"
      :customDelete="true"
      :bulkDeleteApi="true"
    >
      <template #button-filter="{ isSelected, showDeleteModal }">
        <b-row>
          <b-col md="12">
            <ModalFilter
              :activeFilters="activeFilters"
              @handleChangeActiveFilters="handleChangeActiveFilters"
              :isSelected="isSelected"
              :showDeleteModal="showDeleteModal"
            />
          </b-col>
        </b-row>
      </template>
      <template #cell(landId)="data"> {{ data.item ? data.item.code : '-' }}<br /> </template>
      <template #cell(landName)="data"> {{ data.item ? data.item.name : '-' }}<br /> </template>
      <template #cell(description)="data">
        {{ data.item.description ? data.item.description : '-' }}<br />
      </template>
      <template #cell(licensePlate)="data">
        {{ data.item.vehicle ? data.item.vehicle.licensePlate : '-' }}<br />
      </template>
      <template #cell(startTrip)="data">
        {{ data.item ? momentDateShortMonth(data.item.startTrip) : '-' }}<br />
        <span class="text-muted">{{
          data.item ? momentTimeSeconds(data.item.startTrip) : '-'
        }}</span>
      </template>
      <template #cell(endTrip)="data">
        {{ data.item ? momentDateShortMonth(data.item.endTrip) : '-' }}<br />
        <span class="text-muted">{{ data.item ? momentTimeSeconds(data.item.endTrip) : '-' }}</span>
      </template>
      <template #cell(createdBy)="data">
        {{ data.item.createdBy ? data.item.createdBy.fullname : '-' }}<br />
      </template>
      <template #cell(viewDetail)="data">
        <a href="javascript:void(0)" @click="onRowClicked(data.item)">
          <strong class="text-primary">
            {{ $t('landMeasurement.field.showArea') }}
          </strong>
        </a>
      </template>
    </McGrapqlTable>
    <ModalDetail :item="detailData"></ModalDetail>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import McListDownloader from '@/components/McListDownloader'
import ModalFilter from './ModalFilter'
import ModalDetail from './modalDetail'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import {
  GET_ZONES,
  DELETE_LAND_MEASUREMENT,
  REPORT_LAND_MEASUREMENT,
} from '@/services/api/landMeasurement/landMeasurement'
import moment from 'moment'

moment.locale('id')
/**
 * Starter component
 */
export default {
  page: {
    title: 'Land Measurement',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McGrapqlTable,
    McButton,
    McListDownloader,
    ModalFilter,
    ModalDetail,
  },

  mixins: [Helper, MixPanel],

  data() {
    return {
      count: 0,
      totalCount: 0,
      detailData: null,

      activeFilters: {},
      filter: [],

      page: 0,
      show: 0,
      filterList: null,
    }
  },

  computed: {
    title() {
      return this.$t('landMeasurement.title')
    },
    subTitle() {
      return this.$t('landMeasurement.subtitle')
    },
    fields() {
      return [
        {
          key: 'code',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.landId'),
          isSortable: true,
        },
        {
          key: 'name',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.landName'),
          isSortable: true,
        },
        {
          key: 'description',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.description'),
          isSortable: true,
        },
        {
          key: 'startTrip',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.startTime'),
          isSortable: true,
        },
        {
          key: 'endTrip',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.endTime'),
          isSortable: true,
        },
        {
          key: 'licensePlate',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.licensePlate'),
          isSortable: false,
        },
        {
          key: 'createdBy',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.createdBy'),
          isSortable: false,
        },
        {
          key: 'viewDetail',
          thClass: 'mc-table--50',
          label: this.$t('landMeasurement.field.viewDetail'),
          isSortable: false,
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const allFilter = {
            ...filter,
            ...this.activeFilters,
            tripTime:
              this.activeFilters.tripTime &&
              this.activeFilters.tripTime[0] !== null &&
              this.activeFilters.tripTime[1] !== null
                ? {
                    start: moment(this.activeFilters.tripTime[0]).toISOString(),
                    end: moment(this.activeFilters.tripTime[1]).toISOString(),
                  }
                : null,

            vehicleId: this.activeFilters.vehicleId
              ? this.activeFilters.vehicleId.map((id) => parseInt(id))
              : null,
            userId: this.activeFilters.userId
              ? this.activeFilters.userId.map((id) => parseInt(id))
              : null,
          }

          const res = await this.$apollo.query({
            query: GET_ZONES,
            fetchPolicy: 'no-cache',
            variables: {
              filter: allFilter,
            },
          })

          const self = this
          self.filterList = allFilter
          self.page = res.data.zones.zones.pagination.page
          self.show = res.data.zones.zones.pagination.show
          self.count = res.data.zones.zones.pagination.show
          self.totalCount = res.data.zones.zones.pagination.total
          if (self.totalCount < self.count) {
            self.count = res.data.zones.zones.pagination.total
          }
          return res.data.zones.zones
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_LAND_MEASUREMENT,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                zoneId: id.map(Number),
              },
            },
          })

          if (res.data.deleteZone.isSuccessful) {
            this.track('Delete Luas Lahan')
          }
          return res.data.deleteZone.isSuccessful
        },
      }
    },

    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.totalCount, key: 'all' },
      ]
    },
  },

  mounted() {
    this.track('View List Pengukuran Lahan')
  },

  methods: {
    handleAdd() {
      this.$router.push({ name: 'Land Measurement Create' })
    },
    async handleDownload(format, dataAmount) {
      const filter = {
        column: [
          'zoneCode',
          'zoneName',
          'zoneDescription',
          'startTrip',
          'endTrip',
          'licensePlate',
          'createdBy',
        ],
        downloadAs: format,
      }

      if (this.filterList) {
        Object.assign(filter, this.filterList)
      }

      if (dataAmount === 'all') {
        filter.pagination.page = 1
        filter.pagination.show = this.totalCount
      }

      const res = await this.$apollo.query({
        query: REPORT_LAND_MEASUREMENT,
        fetchPolicy: 'no-cache',
        variables: {
          filter: filter,
        },
      })

      if (res.data.zonesReport.isSuccessful) {
        this.track('Download Luas Lahan Data')
        const url = res.data.zonesReport.getZonesReport.report
        window.open(url)
      }
    },

    closeCalendar() {
      this.$refs.periodePicker.defaultOpen = false
    },

    onRowClicked(data) {
      this.detailData = data
      this.$bvModal.show('mc-modal-detail')
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (
          filter.values === null ||
          filter.values === undefined ||
          filter.values === '' ||
          filter.values === false
        )
          return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.filter = filters
    },
  },
}
</script>
